import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Sparkles, MapPin, Phone, ExternalLink, Calendar, Heart, Zap, Shield, Droplets, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import StarRating from "@/components/StarRating";
import ProvidersMap from "@/components/ProvidersMap";
import SEO from "@/components/SEO";

const SITE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const DEFAULT_OG_IMAGE = "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1200&q=80";

// Lowercase, hyphenated slug helper for URLs
const slugify = (s = "") => s.toString().trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

// Related IV therapy services for internal linking (Task 6)
// Slugs must match the SLUG_TO_SERVICE map in ServiceCityPage.jsx
const RELATED_SERVICES = [
  { slug: "mobile-iv-therapy", label: "Mobile IV Therapy" },
  { slug: "hydration-therapy", label: "Hydration Therapy" },
  { slug: "myers-cocktail", label: "Myers Cocktail" },
  { slug: "nad-therapy", label: "NAD+ Therapy" },
  { slug: "immune-boost", label: "Immune Boost IV" },
  { slug: "hangover-recovery", label: "Hangover Recovery" },
  { slug: "athletic-recovery", label: "Athletic Recovery IV" },
  { slug: "anti-aging", label: "Anti-Aging IV" },
];

const STATIC_BENEFITS = [
  { icon: Zap, title: "Sustained energy", body: "Restore B-complex and amino acids that fuel daily performance — without the caffeine crash." },
  { icon: Droplets, title: "Fast hydration", body: "Direct-to-bloodstream fluids rehydrate you faster than drinking water can." },
  { icon: Heart, title: "Faster recovery", body: "Replenish electrolytes and antioxidants after workouts, travel, or late nights." },
  { icon: Shield, title: "Immune support", body: "Therapeutic doses of vitamin C, zinc, and glutathione for prevention or rebound." },
  { icon: Sparkles, title: "Wellness & glow", body: "Combos with glutathione and biotin support skin clarity, hair, and overall vitality." },
];

const STATIC_FAQ_Q = {
  q1: "What is IV therapy?",
  q4: "How long does an IV therapy session take?",
  q5: "What should I expect during my first IV therapy appointment?",
  q6: "Does insurance cover IV therapy?",
  q7: "Are there any side effects or risks with IV therapy?",
  q8: "Who should NOT receive IV therapy?",
};

const STATIC_FAQ_A = {
  q1: "IV therapy is the intravenous delivery of vitamins, minerals, amino acids, antioxidants and hydrating fluids directly into your bloodstream. It bypasses the digestive system, giving near-100% bioavailability and faster onset than oral supplements.",
  q4: "Most wellness drips run 30 to 60 minutes. Larger volumes or specialized infusions like NAD+ can take 1.5 to 3 hours.",
  q5: "Expect a brief health screening, blood pressure check, and consultation. A licensed nurse will place the IV in a vein (usually the arm), and you'll relax in a chair while the drip runs.",
  q6: "Most aesthetic and wellness IV drips are paid out-of-pocket. Some clinical infusions may be partially covered — check with your provider for a Superbill.",
  q7: "Common minor side effects include bruising at the IV site, a cooling sensation, or a vitamin taste in the mouth. Serious risks are rare when performed by licensed nurses with sterile technique.",
  q8: "IV therapy is not recommended for people with congestive heart failure, severe kidney disease, or known allergies to any of the ingredients. Pregnant or breastfeeding patients should consult their physician first.",
};

// 5 service-specific FAQs (Task 5) — applied to every city page
const SERVICE_FAQS = [
  {
    q: "What's in a Myers' Cocktail?",    a: "The Myers' Cocktail is a classic vitamin and mineral blend containing magnesium, calcium, B-complex vitamins, vitamin B12, and vitamin C delivered in saline. It was originally developed by Dr. John Myers to support energy, immune function, and recovery from fatigue. It is one of the most commonly requested wellness drips.",
  },
  {
    q: "What is NAD+ therapy and what are its benefits?",
    a: "NAD+ (nicotinamide adenine dinucleotide) is a coenzyme found in every cell of the body that supports energy production and cellular repair. NAD+ IV therapy delivers this coenzyme directly into the bloodstream and is often used to support mental clarity, energy, and recovery. Infusions typically run 90 minutes to 3 hours depending on dose.",
  },
  {
    q: "How is hangover IV therapy different from drinking water?",
    a: "Hangover IV drips combine saline fluids with electrolytes, B vitamins, and anti-nausea or anti-inflammatory medications when clinically appropriate. Because the fluids and nutrients enter the bloodstream directly, rehydration and symptom relief tend to be faster and more complete than oral rehydration alone.",
  },
  {
    q: "Can IV therapy support immune health?",
    a: "Immune-focused IV drips typically combine high-dose vitamin C, zinc, B-complex, and sometimes glutathione. These nutrients support immune cell function and antioxidant capacity. Many people choose immune drips during cold and flu season, before travel, or when recovering from a recent illness.",
  },
  {
    q: "Is mobile IV therapy as safe as visiting a clinic?",
    a: "Reputable mobile IV providers send licensed registered nurses or paramedics who follow the same sterile protocols and screening procedures used in a clinic. Make sure your provider verifies a medical-director relationship, performs a health screen before infusion, and discloses ingredients and pricing transparently.",
  },
];

export default function CityPage() {
  const params = useParams();
  // Support BOTH /cities/:state/:city/iv-therapy (new canonical) and /iv-therapy/:state/:city (legacy)
  const state = params.state;
  const city = params.city;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/cities/${state}/${city}/iv-therapy`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [state, city]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" /></div>;
  if (notFound || !data) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-semibold text-stone-900">No IV therapy providers here yet</h1>
      <p className="text-stone-600 mt-3">We don't yet have providers in this city. Try a nearby city or browse all locations.</p>
      <Button asChild className="mt-6 bg-[#064E3B] hover:bg-[#053d2f]"><Link to="/search">Browse all locations</Link></Button>
    </div>
  );

  const { city: cityName, state: stateName, content, services, providers } = data;
  const mapMarkers = (providers || [])
    .map((p) => {
      const c = p.location?.coordinates;
      if (!Array.isArray(c) || c.length < 2) return null;
      return { id: p.id, name: p.name, slug: p.slug, city: p.city, state: p.state, lng: c[0], lat: c[1] };
    })
    .filter(Boolean);

  // ---- 15 FAQs (Task 5): 10 city-specific + 5 service-specific ----
  const cityFAQs = [
    { q: STATIC_FAQ_Q.q1, a: STATIC_FAQ_A.q1 },
    { q: `Is IV therapy safe for residents of ${cityName}, ${stateName}?`, a: content.faq_q2 || "" },
    { q: `What are the benefits of IV therapy in ${cityName}?`, a: content.faq_q3 || "" },
    { q: STATIC_FAQ_Q.q4, a: STATIC_FAQ_A.q4 },
    { q: STATIC_FAQ_Q.q5, a: STATIC_FAQ_A.q5 },
    { q: STATIC_FAQ_Q.q6, a: STATIC_FAQ_A.q6 },
    { q: STATIC_FAQ_Q.q7, a: STATIC_FAQ_A.q7 },
    { q: STATIC_FAQ_Q.q8, a: STATIC_FAQ_A.q8 },
    { q: `How often should I get IV therapy in ${cityName}?`, a: content.faq_q9 || "" },
    { q: `How do I book an IV therapy appointment in ${cityName}?`, a: content.faq_q10 || "" },
  ];
  const personalizedFAQs = [...cityFAQs, ...SERVICE_FAQS].filter((f) => f.q && f.a);

  // ---- SEO metadata (Task 1) ----
  const stateAbbr = (data.state_abbr || "").toUpperCase();
  const canonicalPath = `/cities/${slugify(stateName)}/${slugify(cityName)}/iv-therapy`;
  const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;
  // Prefer AI-generated unique meta (Parts 2 & 3); fall back to deterministic templates.
  const titlePrimary = `IV Therapy in ${cityName}, ${stateAbbr} | Mobile IV Therapy Providers`;
  const titleShort = `IV Therapy in ${cityName}, ${stateAbbr} | IV Therapy Finder`;
  const fallbackTitle = titlePrimary.length <= 60 ? titlePrimary : titleShort;
  const metaTitle = (data.meta_title && data.meta_title.length <= 60) ? data.meta_title : fallbackTitle;
  const fallbackDescription = `Find IV Therapy providers in ${cityName}, ${stateAbbr}. Compare hydration therapy, vitamin drips, mobile IV services, and wellness treatments. Book today.`;
  const metaDescription = (data.meta_description && data.meta_description.length >= 120 && data.meta_description.length <= 200) ? data.meta_description : fallbackDescription;
  const ogImage = providers?.find((p) => p.hero_image)?.hero_image || DEFAULT_OG_IMAGE;

  // ---- Breadcrumbs (Task 3) ----
  const breadcrumbItems = [
    { name: "Home", path: "/" },
    { name: stateName, path: `/iv-therapy/${slugify(stateName)}` },
    { name: cityName, path: canonicalPath },
    { name: "IV Therapy", path: null },
  ];
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      ...(b.path ? { item: `${SITE_ORIGIN}${b.path}` } : {}),
    })),
  };

  // ---- LocalBusiness schema per provider (Task 4) ----
  // Generated dynamically from current DB records — no static caching.
  const providerSchemas = (providers || []).map((p) => {
    const coords = p.location?.coordinates;
    const lat = Array.isArray(coords) && coords.length > 1 ? coords[1] : null;
    const lng = Array.isArray(coords) && coords.length > 1 ? coords[0] : null;
    const schema = {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "@id": `${SITE_ORIGIN}/listing/${p.slug}#business`,
      name: p.name,
      url: `${SITE_ORIGIN}/listing/${p.slug}`,
    };
    if (p.description) schema.description = p.description;
    if (p.hero_image) schema.image = p.hero_image;
    if (p.phone) schema.telephone = p.phone;
    if (p.website) schema.sameAs = [p.website];
    if (p.address || p.city) {
      schema.address = {
        "@type": "PostalAddress",
        ...(p.address ? { streetAddress: p.address } : {}),
        addressLocality: p.city || cityName,
        addressRegion: stateAbbr,
        ...(p.zip_code ? { postalCode: p.zip_code } : {}),
        addressCountry: "US",
      };
    }
    if (lat != null && lng != null) {
      schema.geo = { "@type": "GeoCoordinates", latitude: lat, longitude: lng };
    }
    if (p.avg_rating > 0 && p.review_count > 0) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: Number(p.avg_rating).toFixed(1),
        reviewCount: p.review_count,
        bestRating: "5",
        worstRating: "1",
      };
    }
    if (Array.isArray(p.services) && p.services.length) {
      schema.makesOffer = p.services.slice(0, 12).map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "MedicalTherapy", name: s.name || s },
      }));
    }
    return schema;
  });

  // ---- FAQPage schema built dynamically from visible FAQs (Task 5) ----
  // This guarantees the schema always matches what users see — no drift.
  const faqPageSchema = personalizedFAQs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: personalizedFAQs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  return (
    <div className="bg-[#F3F4F1]" data-testid="city-page">
      <SEO
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalUrl}
        ogImage={ogImage}
        ogType="website"
        jsonLd={[faqPageSchema, breadcrumbSchema, ...providerSchemas].filter(Boolean)}
      />
      <section className="bg-gradient-to-br from-[#064E3B] to-[#053d2f] text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <nav aria-label="Breadcrumb" data-testid="city-breadcrumb" className="mb-4">
            <ol className="flex flex-wrap items-center gap-1.5 text-xs text-emerald-100/80">
              {breadcrumbItems.map((b, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  {i > 0 && <ChevronRight className="w-3 h-3 opacity-60" aria-hidden="true" />}
                  {b.path ? (
                    <Link to={b.path} className="hover:text-white underline-offset-2 hover:underline transition-colors">
                      {b.name}
                    </Link>
                  ) : (
                    <span aria-current="page" className="text-white font-medium">{b.name}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-200 font-semibold mb-3">IV Therapy in {stateName}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold mb-4">IV Therapy in {cityName}, {(data.state_abbr || "").toUpperCase()}</h1>
          <p className="text-base sm:text-lg text-emerald-50/90 leading-relaxed max-w-2xl" data-testid="city-intro">{content.introduction}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button asChild size="lg" className="bg-white text-[#064E3B] hover:bg-emerald-50 rounded-full" data-testid="cta-book">
              <a href="#providers"><Calendar className="w-4 h-4 mr-2" /> Book IV Therapy in {cityName}</a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-full" data-testid="cta-view-providers">
              <a href="#providers">View Providers in {cityName}</a>
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        <section data-testid="section-why-fits">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-3">Why IV therapy fits {cityName}</h2>
          <p className="text-stone-700 leading-relaxed text-base sm:text-lg whitespace-pre-line">{content.why_iv_therapy_fits}</p>
        </section>

        <section data-testid="section-benefits">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-5">Benefits of IV therapy</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {STATIC_BENEFITS.map((b) => (
              <div key={b.title} className="bg-white rounded-xl border border-stone-200 p-4">
                <div className="w-9 h-9 rounded-lg bg-[#064E3B]/10 text-[#064E3B] flex items-center justify-center mb-2"><b.icon className="w-4 h-4" /></div>
                <div className="font-display font-semibold text-stone-900 mb-1">{b.title}</div>
                <p className="text-sm text-stone-600 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="section-local-context">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-3">About {cityName}</h2>
          <p className="text-stone-700 leading-relaxed whitespace-pre-line">{content.local_context}</p>
        </section>

        {content.wellness_context && (
          <section data-testid="section-wellness-context">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-3">Local wellness context in {cityName}</h2>
            <p className="text-stone-700 leading-relaxed whitespace-pre-line">{content.wellness_context}</p>
          </section>
        )}

        {content.benefits_local && (
          <section data-testid="section-benefits-local">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-3">How {cityName} residents benefit from IV therapy</h2>
            <p className="text-stone-700 leading-relaxed whitespace-pre-line">{content.benefits_local}</p>
          </section>
        )}

        {content.seasonal_context && (
          <section data-testid="section-seasonal-context">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-3">Seasonal & lifestyle considerations</h2>
            <p className="text-stone-700 leading-relaxed whitespace-pre-line">{content.seasonal_context}</p>
          </section>
        )}

        <section data-testid="section-when">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-3">When to consider IV therapy in {cityName}</h2>
          <p className="text-stone-700 leading-relaxed whitespace-pre-line">{content.why_iv_therapy_fits ? '' : ''}</p>
          <ul className="space-y-2">
            {(content.when_to_consider || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2.5 bg-white rounded-lg border border-stone-200 px-4 py-3">
                <ChevronRight className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" />
                <span className="text-stone-700">{s}</span>
              </li>
            ))}
          </ul>
        </section>

        {services?.length > 0 && (
          <section data-testid="section-services">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-3">IV therapy services available in {cityName}</h2>
            <div className="flex flex-wrap gap-2">
              {services.map((s) => (
                <Badge key={s} className="bg-white border border-stone-200 text-stone-700 hover:bg-stone-50 px-3 py-1.5 text-sm font-normal">{s}</Badge>
              ))}
            </div>
          </section>
        )}

        <section data-testid="section-faqs">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-4">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="bg-white rounded-2xl border border-stone-200">
            {personalizedFAQs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="px-5">
                <AccordionTrigger className="text-left font-medium hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section id="providers" data-testid="section-providers">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-5">IV therapy providers in {cityName}</h2>
          {mapMarkers.length > 0 && (
            <div className="mb-5" data-testid="city-map">
              <ProvidersMap markers={mapMarkers} height="380px" zoom={11} />
            </div>
          )}
          {providers.length === 0 ? (
            <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center text-stone-500 text-sm">No providers yet — be the first to add a listing.</div>
          ) : (
            <div className="space-y-3">
              {providers.map((p) => (
                <Link
                  key={p.id}
                  to={`/location/${p.slug}`}
                  className="block bg-white rounded-xl border border-stone-200 p-5 hover:border-[#064E3B]/40 hover:shadow-md transition"
                  data-testid={`city-provider-${p.id}`}
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    {p.hero_image && (
                      <img src={p.hero_image} alt="" className="w-full sm:w-32 h-32 object-cover rounded-lg" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <h3 className="font-display text-lg font-semibold text-stone-900 hover:text-[#064E3B] transition">{p.name}</h3>
                        <ExternalLink className="w-4 h-4 text-stone-400 shrink-0 mt-1" />
                      </div>
                      {p.avg_rating > 0 && (
                        <div className="flex items-center gap-2 mb-2">
                          <StarRating rating={p.avg_rating} size={14} />
                          <span className="text-xs text-stone-500">({p.review_count || 0} reviews)</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-3 text-xs text-stone-600 mb-2">
                        {p.address && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.address}</span>}
                        {p.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone}</span>}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(p.services || []).slice(0, 5).map((s) => (
                          <span key={s.name} className="text-[11px] bg-stone-100 text-stone-700 px-2 py-0.5 rounded">{s.name}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section data-testid="section-how-to-start">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-4">How to get started</h2>
          <ol className="bg-white rounded-2xl border border-stone-200 p-5 space-y-3">
            {[
              `Browse IV therapy providers in ${cityName} above`,
              "Click on a provider to see reviews, services, and FAQs",
              "Book an appointment online or call directly",
              "Arrive 15 minutes early for your screening",
              "Choose the IV therapy service that fits your needs",
              "Relax during your session — most last 30 to 60 minutes",
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#064E3B] text-white text-xs font-semibold flex items-center justify-center shrink-0">{i+1}</span>
                <span className="text-stone-700">{step}</span>
              </li>
            ))}
          </ol>
        </section>

        <section data-testid="section-payment">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-3">Insurance & payment</h2>
          <div className="bg-white rounded-2xl border border-stone-200 p-5 text-stone-700 leading-relaxed">
            <p>Most wellness IV therapy in {cityName} is paid out-of-pocket. Common payment options include credit cards, HSA/FSA cards, and financing packages.</p>
            <p className="mt-3">Some clinical infusions (for migraine, severe dehydration, documented deficiencies, etc.) may be partially covered by insurance. Ask your provider for a <strong>Superbill</strong> with proper procedure codes for reimbursement.</p>
          </div>
        </section>

        {/* Related services in {city} — internal linking (Task 6) */}
        <section data-testid="section-related-services">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-4">Explore IV therapy services in {cityName}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {RELATED_SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}/${slugify(cityName)}`}
                className="bg-white rounded-xl border border-stone-200 px-4 py-3 hover:border-[#064E3B]/40 hover:shadow-md transition group"
                data-testid={`related-service-${s.slug}`}
              >
                <div className="font-display font-semibold text-stone-900 text-sm group-hover:text-[#064E3B]">{s.label}</div>
                <div className="text-xs text-stone-500 mt-0.5">in {cityName}</div>
              </Link>
            ))}
          </div>
        </section>

        {/* Nearby cities — internal linking (Task 6) */}
        {data.nearby_cities?.length > 0 && (
          <section data-testid="section-nearby-cities">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-4">Nearby IV therapy cities in {stateName}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.nearby_cities.map((c) => (
                <Link
                  key={c.city}
                  to={`/cities/${slugify(c.state)}/${slugify(c.city)}/iv-therapy`}
                  className="bg-white rounded-xl border border-stone-200 px-4 py-3 hover:border-[#064E3B]/40 hover:shadow-md transition group"
                  data-testid={`nearby-city-${slugify(c.city)}`}
                >
                  <div className="font-display font-semibold text-stone-900 text-sm group-hover:text-[#064E3B]">{c.city}</div>
                  <div className="text-xs text-[#10B981] font-semibold uppercase tracking-wider mt-0.5">{c.count} provider{c.count !== 1 ? "s" : ""}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="bg-[#064E3B] rounded-3xl px-6 py-10 text-center text-white" data-testid="section-cta">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold mb-2">Ready for IV therapy in {cityName}?</h2>
          <p className="text-emerald-100 mb-6">Compare the top providers in your area and book your appointment today.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild className="bg-white text-[#064E3B] hover:bg-emerald-50 rounded-full"><a href="#providers">Book IV Therapy in {cityName}</a></Button>
            <Button asChild variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-full"><Link to="/search">Browse All Locations</Link></Button>
          </div>
        </section>
      </div>
    </div>
  );
}
